import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useNavigate } from "react-router-dom";
import {
  IconSearch,
  IconUsers,
  IconSpeakerphone,
  IconArrowRight,
  IconRobot,
  IconLoader,
  IconArrowsMaximize,
} from "@tabler/icons-react";
import { cn } from "../../lib/utils";
import { useAgentChat } from "../../contexts/AgentChatContext";
import {
  getPermittedCommandActions,
  getSearchableNavigationItems,
} from "../Navigation/constants";
import { useAuth } from "../../contexts/auth/AuthContext";

const CommandPaletteContext = React.createContext({ open: false, setOpen: () => {} });

export function useCommandPalette() {
  return React.useContext(CommandPaletteContext);
}

export function CommandPaletteProvider({ children, campaigns = [], creators = [] }) {
  const [open, setOpen] = React.useState(false);

  // Global Cmd+K / Ctrl+K listener
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <CommandPaletteContext.Provider value={{ open, setOpen }}>
      {children}
      <CommandPalette
        open={open}
        onOpenChange={setOpen}
        campaigns={campaigns}
        creators={creators}
      />
    </CommandPaletteContext.Provider>
  );
}

function CommandPalette({ open, onOpenChange, campaigns = [], creators = [] }) {
  const [query, setQuery] = React.useState("");
  const [activeIndex, setActiveIndex] = React.useState(0);
  const [aiResponse, setAiResponse] = React.useState(null);
  const [aiLoading, setAiLoading] = React.useState(false);
  const navigate = useNavigate();
  const inputRef = React.useRef(null);
  const listRef = React.useRef(null);
  const returnFocusRef = React.useRef(null);
  const restoreFocusRef = React.useRef(true);
  const mountedRef = React.useRef(false);

  React.useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const agentChat = useAgentChat();
  const { hasPermission, user } = useAuth();
  const navigationPages = React.useMemo(
    () => getSearchableNavigationItems(hasPermission, user),
    [hasPermission, user]
  );
  const commandActions = React.useMemo(
    () => getPermittedCommandActions(hasPermission, user),
    [hasPermission, user]
  );

  // Build filtered results grouped by category
  const groups = React.useMemo(() => {
    const q = query.toLowerCase().trim();
    const result = [];

    // Registered pages, retaining the same Creator Hub / Operations grouping as the shell.
    [...commandActions, ...navigationPages].forEach((page) => {
      const searchableText = [page.label, page.commandLabel, ...(page.aliases || [])]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      if (!searchableText.includes(q)) return;

      let group = result.find(candidate => candidate.label === page.groupLabel);
      if (!group) {
        group = { label: page.groupLabel, items: [] };
        result.push(group);
      }
      group.items.push({ ...page, label: page.commandLabel || page.label });
    });

    // Campaigns
    if (campaigns.length) {
      const filtered = campaigns
        .filter((c) => (c.name || c.title || "").toLowerCase().includes(q))
        .slice(0, 5)
        .map((c) => ({
          id: `campaign-${c._id}`,
          label: c.name || c.title,
          path: `/campaigns/${c._id}`,
          icon: IconSpeakerphone,
        }));
      if (filtered.length) result.push({ label: "Campaigns", items: filtered });
    }

    // Creators
    if (creators.length) {
      const filtered = creators
        .filter((c) => {
          const name = (c.twitterHandle || c.name || "").toLowerCase();
          return name.includes(q);
        })
        .slice(0, 5)
        .map((c) => ({
          id: `creator-${c._id}`,
          label: `@${c.twitterHandle || c.name}`,
          path: "/creators",
          icon: IconUsers,
        }));
      if (filtered.length) result.push({ label: "Creators", items: filtered });
    }

    return result;
  }, [query, campaigns, creators, navigationPages, commandActions]);

  // Flat list for keyboard nav — add AI item at the end when there's a query
  const flatItems = React.useMemo(() => {
    const items = groups.flatMap((g) => g.items);
    if (query.trim() && agentChat) {
      items.push({ id: "ask-ai", label: `Ask AI: "${query.trim()}"`, icon: IconRobot, isAI: true });
    }
    return items;
  }, [groups, query, agentChat]);

  // Reset state when opening
  React.useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIndex(0);
      setAiResponse(null);
      setAiLoading(false);
    }
  }, [open]);

  // Scroll active item into view
  React.useEffect(() => {
    if (!listRef.current) return;
    const active = listRef.current.querySelector('[data-active="true"]');
    if (active) active.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  const selectItem = React.useCallback(
    async (item) => {
      if (item.isAI && agentChat) {
        // Send to AI
        setAiLoading(true);
        setAiResponse(null);
        const response = await agentChat.sendMessage(query.trim());
        setAiLoading(false);
        if (response) {
          setAiResponse(response);
        }
        return; // Don't close
      }
      restoreFocusRef.current = Boolean(item.external);
      onOpenChange(false);
      if (item.external) {
        window.open(item.path, '_blank', 'noopener,noreferrer');
        return;
      }
      navigate(item.path);
    },
    [navigate, onOpenChange, query, agentChat]
  );

  const handleExpandToSidebar = React.useCallback(() => {
    restoreFocusRef.current = false;
    onOpenChange(false);
    if (agentChat) {
      agentChat.openSidebar();
    }
  }, [onOpenChange, agentChat]);

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % Math.max(flatItems.length, 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + flatItems.length) % Math.max(flatItems.length, 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (flatItems[activeIndex]) selectItem(flatItems[activeIndex]);
    }
  };

  let itemIndex = -1;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="ui-motion-overlay fixed inset-0 z-50 bg-black/60" />
        <DialogPrimitive.Content
          className="ui-motion-modal ui-motion-positioned fixed left-[50%] top-[20%] z-50 w-full max-w-lg translate-x-[-50%] border border-glass-border bg-surface-1"
          onKeyDown={handleKeyDown}
          onOpenAutoFocus={() => {
            returnFocusRef.current = document.activeElement;
            restoreFocusRef.current = true;
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            if (mountedRef.current && restoreFocusRef.current && returnFocusRef.current?.isConnected) {
              returnFocusRef.current.focus({ preventScroll: true });
            }
          }}
          aria-label="Command palette"
        >
          {/* Visually hidden title for accessibility */}
          <DialogPrimitive.Title className="sr-only">
            Command palette
          </DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            Search permitted Creator Hub and Operations destinations
          </DialogPrimitive.Description>

          {/* Search input */}
          <div className="flex items-center border-b border-glass-border px-4">
            <IconSearch className="mr-3 h-4 w-4 shrink-0 text-text-tertiary" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setActiveIndex(0);
                setAiResponse(null);
              }}
              placeholder="Search or ask AI anything..."
              aria-label="Search destinations or ask AI"
              className="flex h-12 w-full bg-transparent text-sm text-text-primary placeholder:text-text-tertiary outline-none"
            />
            <kbd className="ml-2 shrink-0 border border-glass-border bg-surface-2 px-1.5 py-0.5 text-[10px] font-medium text-text-tertiary">
              ESC
            </kbd>
          </div>

          {/* Results */}
          <div
            ref={listRef}
            className="max-h-80 overflow-y-auto overscroll-contain py-2"
          >
            {/* AI Response (shown inline after asking) */}
            {(aiLoading || aiResponse) && (
              <div className="px-4 py-3 border-b border-glass-border">
                <div className="flex items-center gap-1.5 mb-2">
                  <IconRobot className="h-3.5 w-3.5 text-accent" />
                  <span className="text-xs font-medium text-accent uppercase tracking-wider">AI Agent</span>
                  {aiResponse?.toolCalls?.length > 0 && (
                    <span className="text-[10px] text-text-tertiary">
                      &middot; {aiResponse.toolCalls.join(', ')}
                    </span>
                  )}
                </div>
                {aiLoading ? (
                  <div className="flex items-center gap-2 py-2">
                    <IconLoader className="h-4 w-4 animate-spin text-accent" />
                    <span className="text-sm text-text-tertiary">Thinking...</span>
                  </div>
                ) : aiResponse ? (
                  <div>
                    <div className={cn(
                      "text-sm leading-relaxed whitespace-pre-wrap max-h-48 overflow-y-auto",
                      aiResponse.isError ? "text-error" : "text-text-primary"
                    )}>
                      {aiResponse.content}
                    </div>
                    <button
                      onClick={handleExpandToSidebar}
                      className="mt-2 flex items-center gap-1.5 text-xs text-accent hover:text-accent-hover"
                    >
                      <IconArrowsMaximize className="h-3 w-3" />
                      Open in sidebar for follow-up
                    </button>
                  </div>
                ) : null}
              </div>
            )}

            {flatItems.length === 0 && !aiLoading && !aiResponse && (
              <p className="px-4 py-6 text-center text-sm text-text-tertiary">
                No results found.
              </p>
            )}
            {groups.map((group) => (
              <div key={group.label}>
                <div className="px-4 py-1.5 text-xs font-medium text-text-tertiary uppercase tracking-wider">
                  {group.label}
                </div>
                {group.items.map((item) => {
                  itemIndex++;
                  const idx = itemIndex;
                  const isActive = idx === activeIndex;
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.id}
                      data-active={isActive}
                      className={cn(
                        "flex w-full items-center gap-3 px-4 py-2.5 text-sm text-text-primary cursor-pointer",
                        isActive
                          ? "bg-surface-3 text-text-primary"
                          : "hover:bg-surface-2"
                      )}
                      onClick={() => selectItem(item)}
                      onMouseEnter={() => setActiveIndex(idx)}
                    >
                      <Icon className="h-4 w-4 shrink-0 text-text-tertiary" />
                      <span className="flex-1 text-left truncate">{item.label}</span>
                      {isActive && (
                        <IconArrowRight className="h-3.5 w-3.5 text-text-tertiary" />
                      )}
                    </button>
                  );
                })}
              </div>
            ))}

            {/* Ask AI option — always at the bottom when there's a query */}
            {query.trim() && agentChat && (
              <div>
                <div className="px-4 py-1.5 text-xs font-medium text-text-tertiary uppercase tracking-wider">
                  AI Agent
                </div>
                {(() => {
                  itemIndex++;
                  const idx = itemIndex;
                  const isActive = idx === activeIndex;
                  return (
                    <button
                      data-active={isActive}
                      className={cn(
                        "flex w-full items-center gap-3 px-4 py-2.5 text-sm cursor-pointer",
                        isActive
                          ? "bg-accent/10 text-accent"
                          : "text-text-primary hover:bg-surface-2"
                      )}
                      onClick={() => selectItem(flatItems[idx])}
                      onMouseEnter={() => setActiveIndex(idx)}
                    >
                      <IconRobot className={cn("h-4 w-4 shrink-0", isActive ? "text-accent" : "text-text-tertiary")} />
                      <span className="flex-1 text-left truncate">
                        Ask AI: <span className="text-text-secondary italic">"{query.trim()}"</span>
                      </span>
                      {aiLoading ? (
                        <IconLoader className="h-3.5 w-3.5 animate-spin text-accent" />
                      ) : isActive ? (
                        <IconArrowRight className="h-3.5 w-3.5 text-accent" />
                      ) : null}
                    </button>
                  );
                })()}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between border-t border-glass-border px-4 py-2 text-[11px] text-text-tertiary">
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1">
                <kbd className="border border-glass-border bg-surface-2 px-1 py-0.5 text-[10px]">↑↓</kbd>
                navigate
              </span>
              <span className="flex items-center gap-1">
                <kbd className="border border-glass-border bg-surface-2 px-1 py-0.5 text-[10px]">↵</kbd>
                select
              </span>
            </div>
            <span className="flex items-center gap-1">
              <kbd className="rounded border border-glass-border bg-surface-2 px-1 py-0.5 text-[10px]">esc</kbd>
              close
            </span>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export { CommandPalette };
